import { Lock } from '@mui/icons-material'
import { Box, Tabs, Tooltip } from '@mui/material'
import { ReactElement } from 'react'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { NestedRoute, Route, SecondLevelRoute } from 'src/routing/routing'
import { isNestedRouteAllowedForRole } from 'src/routing/routing-utils'
import { TabNavLink } from 'src/routing/TabNavLink'
import { USER_ROLES_TYPE } from 'src/shared/constants/constants'
import { Utils } from 'src/shared/utils/Utils'
import { useUser } from 'src/user/UserContext'
import styled from 'styled-components'

export const SECOND_LEVEL_NAVIGATION_OFFSET_TOP = '85px'
export const SecondLevelNavigationContainer = styled(Box)<{ $showNavigation: boolean }>`
  margin-top: ${({ $showNavigation }) => ($showNavigation ? SECOND_LEVEL_NAVIGATION_OFFSET_TOP : '0px')};
`

interface SecondLevelNavigationProps {
  route: Route
  baseUrl: string
  activeRoute: SecondLevelRoute
  isNestedRouteAllowed?: (nestedRoute: SecondLevelRoute) => boolean
}

const TabsStyled = styled(Tabs)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey.divider};
  width: 100%;

  & .MuiTabScrollButton-root {
    color: ${({ theme }) => theme.colors.text.secondary};

    &:hover {
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }
`

export const SecondLevelNavigation = ({
  route,
  baseUrl,
  activeRoute,
  isNestedRouteAllowed,
}: SecondLevelNavigationProps): ReactElement => {
  const { user } = useUser()
  const userRoles = user.roles

  const nested = route?.nested as NestedRoute

  const filteredRoutes =
    Object.keys(nested).filter((key) => {
      const nestedRoute = nested[key]

      const isNestedRouteAllowedBasedOnRole = isNestedRouteAllowedForRole(nestedRoute, userRoles)
      const isNestedRouteAllowedBasedOnAdditionalValidation = isNestedRouteAllowed
        ? isNestedRouteAllowed(nestedRoute)
        : true

      return (
        !nestedRoute.hideNavigation &&
        isNestedRouteAllowedBasedOnRole &&
        isNestedRouteAllowedBasedOnAdditionalValidation
      )
    }) || []

  const { getMessage } = useMessageSource()

  const showViewableOnlyByGfchIcon = (
    userRoles: USER_ROLES_TYPE[],
    parentRouteRoles: USER_ROLES_TYPE[],
    nestedRouteRoles: USER_ROLES_TYPE[],
  ) => {
    // if parent route is viewable only for gfch, we don't need to propagate the icon it on the tabs
    if (Utils.isViewableOnlyForGfch(userRoles, parentRouteRoles)) {
      return false
    }

    return Utils.isViewableOnlyForGfch(userRoles, nestedRouteRoles)
  }

  return (
    <TabsStyled value={activeRoute} textColor="inherit" variant="scrollable" scrollButtons="auto">
      {filteredRoutes.map((nestedRouteKey) => (
        <TabNavLink
          to={`${baseUrl}/${nested[nestedRouteKey].path}`}
          value={nested[nestedRouteKey]}
          label={getMessage(nested[nestedRouteKey].navigationKey || '')}
          key={nested[nestedRouteKey].path}
          icon={
            showViewableOnlyByGfchIcon(
              userRoles,
              route.roles as USER_ROLES_TYPE[],
              nested[nestedRouteKey].roles as USER_ROLES_TYPE[],
            ) ? (
              <Tooltip title={getMessage('label.navigation.gfch.only.tooltip')} placement="right-end">
                <Lock color="inherit" fontSize="inherit" />
              </Tooltip>
            ) : undefined
          }
          iconPosition="end"
        />
      ))}
    </TabsStyled>
  )
}
